import { Box } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import GeoMapWithTiles from '../../../features/geo-map/GeoMapWithTiles';
import RouteVectorLayer from '../../../shared/ui/GeoMap/RouteVectorLayer';
import PolygonVectorLayer from '../../../shared/ui/GeoMap/PolygonVectorLayer';
import MarkerVectorLayer from '../../../shared/ui/GeoMap/MarkerVectorLayer';
import style from './DivisionVehicleMapWidget.module.css';
import splitRoutes from '../../../shared/utils/splitRoutes';
import transformCoords from '../../../shared/utils/transformCoords';
import mapNames from '../../../entities/map/constants';
import convertFormatDate from '../../../shared/utils/convertFormatDate';
import useTimeZone from '../../../entities/timeZone/hooks/useTimeZone';
import { generateColorWithoutRed } from '../../../shared/utils/generateColor';
import { getCSSOpacityByDates } from '../../../shared/utils/getCSSOpacityByDates';

function DivisionVehicleMapWidget({
  mapName,
  vehicles,
  vehiclesInfo,
  vehicleTracksMap,
  coordsState,
  zoom,
  extent,
  isAnimateOnExtent,
  geoZones,
  enableGeozoneNames,
  enableMapRoutes,
  isNeedHideGozoneNames,
  speedLimit,
  stopTime,
  markers,
  handleClickMap,
  currentDate,
}) {
  const isPlayerWorking = !!coordsState.length;
  const timeZone = useTimeZone();

  // Получаем информацию о транспортном средстве по ID
  const getVehicleInfo = useCallback(
    (vehicleId) => {
      return vehiclesInfo?.[vehicleId] || {};
    },
    [vehiclesInfo],
  );

  // Определяем текущую позицию для каждого ТС из vehicleTracksMap на основе текущего времени
  const currentPositionByVehicle = useMemo(() => {
    const currentByVehicle = {};

    if (!coordsState || coordsState.length === 0) {
      return currentByVehicle;
    }

    // Получаем текущее время из последней координаты в coordsState
    const lastCoord = coordsState[coordsState.length - 1];
    const currentTime = lastCoord?.date;

    // Для каждого vehicleId находим его позицию на текущий момент времени
    if (vehicleTracksMap && Object.keys(vehicleTracksMap).length > 0) {
      Object.keys(vehicleTracksMap).forEach((vehicleId) => {
        const vehicleCoords = vehicleTracksMap[vehicleId];
        if (vehicleCoords && vehicleCoords.length > 0) {
          // Находим последнюю точку до текущего времени
          // Координаты уже отсортированы по времени, поэтому ищем с конца
          const currentPosition = vehicleCoords
            .slice()
            .reverse()
            .find((coord) => {
              const pointTime = coord.date;
              return !currentTime || new Date(pointTime) <= new Date(currentTime);
            });

          currentByVehicle[Number(vehicleId)] = currentPosition;
        }
      });
    }

    return currentByVehicle;
  }, [coordsState, vehicleTracksMap]);

  // Получаем позиции всех ТС для заданного момента времени
  const getPositionsByMarkerTime = useCallback(
    (markerTime) => {
      const positionsByVehicle = {};

      if (!markerTime || !vehicleTracksMap || Object.keys(vehicleTracksMap).length === 0) {
        return positionsByVehicle;
      }

      vehicles?.forEach((vehicle) => {
        const vehicleCoords = vehicleTracksMap[vehicle.vehicleId];
        if (vehicleCoords && vehicleCoords.length > 0) {
          // Находим последнюю точку ТС до времени маркера
          const position = vehicleCoords
            .slice()
            .reverse()
            .find((coord) => {
              const pointTime = coord.date;
              return new Date(pointTime) <= new Date(markerTime);
            });

          positionsByVehicle[vehicle.vehicleId] = position;
        }
      });

      return positionsByVehicle;
    },
    [vehicles, vehicleTracksMap],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <GeoMapWithTiles
        name={mapName}
        isAnimateOnExtent={isAnimateOnExtent}
        styles={style.geoMap}
        zoom={zoom}
        extent={extent}
        onClick={handleClickMap}
      >
        {/* Отображаем маршруты для каждого ТС из vehicleTracksMap */}
        {enableMapRoutes &&
          vehicles?.map((vehicle, i) => {
            const vehicleCoords = vehicleTracksMap?.[vehicle.vehicleId];

            const lastCoord = coordsState?.[coordsState.length - 1];
            const currentTime = lastCoord?.date;

            const filteredCoords = vehicleCoords?.filter((coord) => {
              const pointTime = coord.date;
              return !currentTime || new Date(pointTime) <= new Date(currentTime);
            });

            if (!vehicleCoords || vehicleCoords.length === 0) {
              return null;
            }

            const curColor = generateColorWithoutRed(i, vehicles.length);

            return (
              <RouteVectorLayer
                key={`route-${vehicle.vehicleId}`}
                coordinatesWithDate={
                  filteredCoords?.length &&
                  splitRoutes(filteredCoords).map((currentCoordinates) =>
                    currentCoordinates.map((point) => ({
                      coordinates: transformCoords(point.longitude, point.latitude),
                      date: point.date,
                    })),
                  )
                }
                vehicleId={vehicle.vehicleId}
                isFollowReverseMode
                isManyRoutesMode
                speedLimit={speedLimit}
                stopTime={stopTime}
                color={curColor}
              />
            );
          })}

        {geoZones?.map((geoZone) => (
          <PolygonVectorLayer
            id={geoZone.geozoneId}
            key={geoZone.id}
            coordinates={geoZone.polygon}
            text={enableGeozoneNames && !isNeedHideGozoneNames ? geoZone.name : ''}
            disableAutoZoom
            zIndex={5}
          />
        ))}

        {/* Отображаем маркеры текущих позиций каждого ТС */}
        {vehicles?.map((vehicle) => {
          const currentPosition = isPlayerWorking
            ? currentPositionByVehicle[vehicle.vehicleId]
            : vehicleTracksMap?.[vehicle.vehicleId]?.[vehicleTracksMap[vehicle.vehicleId].length - 1];

          if (!currentPosition) {
            return null;
          }
          const opacity = getCSSOpacityByDates(currentDate, currentPosition.date);

          const vehicleInfo = getVehicleInfo(vehicle.vehicleId);

          return (
            <MarkerVectorLayer
              key={`marker-${vehicle.vehicleId}`}
              text={vehicleInfo.num || vehicle.name || `ТС ${vehicle.vehicleId}`}
              vehicleCustomIcon={vehicle?.model?.icon}
              vehicleModelId={vehicle?.model?.vehicleModelId}
              vehicleIconColor={`#${vehicle?.customAugmentationEx?.view?.['icon-color'] || '000000'}`}
              coordinates={transformCoords(currentPosition.longitude, currentPosition.latitude)}
              zIndex={5}
              opacity={opacity}
            />
          );
        })}

        {/* Обозначение маркеров с временем - отображаем все ТС в момент времени маркера */}
        {markers?.map((markerIndex) => {
          const coord = coordsState[markerIndex];
          if (!coord) return null;

          const markerTime = coord.date;
          const markerFormatTime = convertFormatDate?.(markerTime, timeZone)?.toFormat || '';
          const positionsByVehicle = getPositionsByMarkerTime(markerTime);

          return vehicles?.map((vehicle) => {
            const currentPosition = positionsByVehicle[vehicle.vehicleId];
            if (!currentPosition) {
              return null;
            }

            const vehicleInfo = getVehicleInfo(vehicle.vehicleId);

            return (
              <MarkerVectorLayer
                key={`history-marker-${markerIndex}-${vehicle.vehicleId}`}
                vehicleModelId={vehicle?.model?.vehicleModelId}
                vehicleIconColor={`#${vehicle?.customAugmentationEx?.view?.['icon-color'] || '000000'}`}
                text={`${vehicleInfo.num || vehicle.name || vehicle.vehicleId}: ${markerFormatTime}`}
                coordinates={transformCoords(currentPosition.longitude, currentPosition.latitude)}
                zIndex={5}
              />
            );
          });
        })}
      </GeoMapWithTiles>
    </Box>
  );
}

DivisionVehicleMapWidget.propTypes = {
  mapName: PropTypes.string,
  vehicles: PropTypes.arrayOf(
    PropTypes.shape({
      vehicleId: PropTypes.number,
      vehicleNum: PropTypes.string,
      model: PropTypes.shape({
        icon: PropTypes.string,
        vehicleModelId: PropTypes.number,
      }),
      customAugmentationEx: PropTypes.shape({
        view: PropTypes.shape({
          'icon-color': PropTypes.string,
        }),
      }),
    }),
  ),
  vehiclesInfo: PropTypes.shape({}),
  vehicleTracksMap: PropTypes.shape({}),
  coordsState: PropTypes.arrayOf(
    PropTypes.shape({
      vehicleId: PropTypes.number,
      longitude: PropTypes.number,
      latitude: PropTypes.number,
      date: PropTypes.string,
    }),
  ),
  zoom: PropTypes.number,
  extent: PropTypes.arrayOf(PropTypes.number),
  isAnimateOnExtent: PropTypes.bool,
  geoZones: PropTypes.arrayOf(PropTypes.shape({})),
  enableGeozoneNames: PropTypes.bool,
  enableMapRoutes: PropTypes.bool,
  isNeedHideGozoneNames: PropTypes.bool,
  speedLimit: PropTypes.number,
  stopTime: PropTypes.number,
  markers: PropTypes.arrayOf(PropTypes.number),
  handleClickMap: PropTypes.func,
  currentDate: PropTypes.string,
};

DivisionVehicleMapWidget.defaultProps = {
  mapName: mapNames.vehicleStory,
  vehicles: [],
  vehiclesInfo: {},
  vehicleTracksMap: {},
  coordsState: [],
  zoom: 10,
  extent: null,
  isAnimateOnExtent: true,
  geoZones: [],
  enableGeozoneNames: true,
  enableMapRoutes: true,
  isNeedHideGozoneNames: false,
  speedLimit: 0,
  stopTime: 0,
  markers: [],
  handleClickMap: null,
  currentDate: '',
};

export default DivisionVehicleMapWidget;
